import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useQueryGetAccountListByFilter } from '../graphQL/hooks/useQueryGetAccountListByFilter';
import { useQueryGetSettlement } from '../graphQL/hooks/useQueryGetSettlement';
import { cloneDeep } from 'lodash';
import { referredType } from '../components/SettlementTable';
import {
  SettlementsAccountEdges,
  SettlementAccount,
} from 'containers/SettlementRequest/graphQL/types';
import { errorHandler } from 'utils/ErrorHandler';

export enum FilterType {
  DATE = 'date',
  TYPE = 'type',
  PRODUCT = 'product',
}

export const useSettlementDetailHooks = () => {
  const params = useParams<{ id: string }>();

  const { settlementItem } = useQueryGetSettlement(params.id as string);
  const { accountList, isLoading, refetchAccountList } =
    useQueryGetAccountListByFilter(params.id as string);

  const [settlementAccountList, setSettlementAccountList] =
    useState<SettlementAccount>(accountList);
  const [filterType, setFilterType] = useState<FilterType>(FilterType.DATE);
  const [page, setPage] = useState<number>(1);
  const [sortedAccountList, setSortedAccountList] = useState<
    SettlementsAccountEdges[]
  >([]);

  const handleAccountListFilter = (filterType: FilterType) => {
    setFilterType(filterType);
  };

  const handlePageChange = () => {
    setPage(page + 1);
  };

  const handleRefetchAccountList = async () => {
    try {
      const response = await refetchAccountList({
        page,
        size: 50,
        filter: {
          settlementIds: [params.id as string],
        },
      });

      if (response) {
        setSettlementAccountList({
          ...settlementAccountList,
          settlement: {
            ...settlementAccountList.settlement,
            accounts: {
              ...settlementAccountList.settlement.accounts,
              edges: [
                ...settlementAccountList.settlement.accounts.edges,
                ...response.data.settlement.accounts.edges,
              ],
            },
          },
        });
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  useEffect(() => {
    if (!isLoading) {
      setSettlementAccountList(accountList);
    }
  }, [isLoading]);

  useEffect(() => {
    if (page !== 1) {
      handleRefetchAccountList();
    }
  }, [page]);

  useEffect(() => {
    if (settlementAccountList) {
      const sortedItems = cloneDeep(
        settlementAccountList.settlement.accounts.edges,
      );

      switch (filterType) {
        case FilterType.DATE: {
          return setSortedAccountList(
            sortedItems.sort((prev, curr) =>
              prev.node.orderItem.transaction.firmedAt >
              curr.node.orderItem.transaction.firmedAt
                ? -1
                : 1,
            ),
          );
        }
        case FilterType.TYPE: {
          return setSortedAccountList(
            sortedItems.sort((prev, curr) =>
              referredType[prev.node.referredType] >
              referredType[curr.node.referredType]
                ? -1
                : 1,
            ),
          );
        }
        case FilterType.PRODUCT: {
          return setSortedAccountList(
            sortedItems.sort((prev, curr) =>
              prev.node.title > curr.node.title ? -1 : 1,
            ),
          );
        }
        default:
          return;
      }
    }
  }, [settlementAccountList, filterType]);

  return {
    settlementItem,
    settlementAccountList,
    sortedAccountList,
    filterType,
    handleAccountListFilter,
    handlePageChange,
  };
};
